/* eslint-disable no-console */
import api from '@pray/shared/utils/api';

import type {
  SolomonRecommendationsRequest,
  SolomonRecommendationsResponse,
  SolomonExecuteActionRequest,
  SolomonExecuteActionResponse,
  SolomonAcceptActionRequest,
  SolomonAcceptActionResponse,
  SolomonPageContext,
  SolomonSubPage,
  SolomonUnifiedResponse,
  SolomonApprovalResponse,
} from '../types/solomon';

interface ApiResponse<T> {
  responded_at: string;
  object: string;
  data: T;
}

const BASE_PATH = '/web/studio/artists';

export const solomonService = {
  /**
   * Get Solomon recommendations for a specific page context
   */
  async getRecommendations({
    artistId,
    pageContext,
    currentState,
    artistName,
  }: {
    artistId: string;
    pageContext: SolomonPageContext;
    currentState: Record<string, unknown>;
    artistName?: string;
  }): Promise<SolomonRecommendationsResponse> {
    const url = `${BASE_PATH}/${artistId}/solomon/recommendations`;
    const body: SolomonRecommendationsRequest = {
      pageContext,
      currentState,
      artistName,
    };

    const response = await api.executePostRequest(url, body as unknown as Record<string, unknown>);
    const apiResponse = response.data as ApiResponse<SolomonRecommendationsResponse>;

    return apiResponse.data;
  },

  /**
   * Execute a Solomon action (e.g., generate an image)
   */
  async executeAction({
    artistId,
    recommendationId,
    actionType,
    params,
  }: {
    artistId: string;
  } & SolomonExecuteActionRequest): Promise<SolomonExecuteActionResponse> {
    const url = `${BASE_PATH}/${artistId}/solomon/execute`;
    const body = {
      recommendationId,
      actionType,
      params,
    };

    // Debug logging for Solomon action execution
    if (process.env.NODE_ENV === 'development') {
      console.log('[Solomon] Executing action:', { url, recommendationId, actionType, params });
    }

    try {
      const response = await api.executePostRequest(url, body);
      const apiResponse = response.data as ApiResponse<SolomonExecuteActionResponse>;

      if (process.env.NODE_ENV === 'development') {
        console.log('[Solomon] Execute response:', apiResponse.data);
      }

      return apiResponse.data;
    } catch (error) {
      console.error('[Solomon] Failed to execute action:', error);
      throw error;
    }
  },

  /**
   * Accept a Solomon action result and persist it to the database
   */
  async acceptAction({
    artistId,
    recommendationId,
    actionType,
    result,
    targetField,
  }: {
    artistId: string;
  } & SolomonAcceptActionRequest): Promise<SolomonAcceptActionResponse> {
    const url = `${BASE_PATH}/${artistId}/solomon/accept`;
    const body = {
      recommendationId,
      actionType,
      result,
      targetField,
    };

    if (process.env.NODE_ENV === 'development') {
      console.log('[Solomon] Accepting action:', { url, recommendationId, actionType, targetField });
    }

    try {
      const response = await api.executePostRequest(url, body);
      const apiResponse = response.data as ApiResponse<SolomonAcceptActionResponse>;

      if (process.env.NODE_ENV === 'development') {
        console.log('[Solomon] Accept response:', apiResponse.data);
      }

      return apiResponse.data;
    } catch (error) {
      console.error('[Solomon] Failed to accept action:', error);
      throw error;
    }
  },

  /**
   * Get Solomon insights for a specific page context (unified agent)
   */
  async getInsights({
    artistId,
    tab,
    subPage,
    pageData,
    sessionId,
    prompt,
    entityId,
    entityType,
  }: {
    artistId: string;
    tab: SolomonPageContext;
    subPage?: SolomonSubPage;
    pageData?: Record<string, unknown>;
    sessionId?: string;
    prompt?: string;
    /** Entity ID if viewing a specific item (e.g., campaign ID, episode ID) */
    entityId?: string;
    /** Type of entity being viewed */
    entityType?: 'episode' | 'series' | 'segment' | 'campaign' | 'workflow';
  }): Promise<SolomonUnifiedResponse> {
    const url = `${BASE_PATH}/${artistId}/solomon/insights`;
    const body = {
      tab,
      subPage,
      pageData,
      sessionId,
      prompt,
      entityId,
      entityType,
    };

    if (process.env.NODE_ENV === 'development') {
      console.log('[Solomon] Getting insights:', { url, tab, subPage });
    }

    try {
      const response = await api.executePostRequest(url, body);
      const apiResponse = response.data as ApiResponse<SolomonUnifiedResponse>;

      if (process.env.NODE_ENV === 'development') {
        console.log('[Solomon] Insights response:', apiResponse.data);
      }

      return apiResponse.data;
    } catch (error) {
      console.error('[Solomon] Failed to get insights:', error);
      throw error;
    }
  },

  /**
   * Approve or reject a pending Solomon action
   */
  async processApproval({
    artistId,
    approvalToken,
    approved,
    rejectionReason,
    sessionId,
  }: {
    artistId: string;
    approvalToken: string;
    approved: boolean;
    rejectionReason?: string;
    sessionId?: string;
  }): Promise<SolomonApprovalResponse> {
    const url = `${BASE_PATH}/${artistId}/solomon/approve`;
    const body = {
      approvalToken,
      approved,
      rejectionReason,
      sessionId,
    };

    if (process.env.NODE_ENV === 'development') {
      console.log('[Solomon] Processing approval:', {
        url,
        approved,
        approvalToken: `${approvalToken.slice(0, 20)}...`,
      });
    }

    try {
      const response = await api.executePostRequest(url, body);
      const apiResponse = response.data as ApiResponse<SolomonApprovalResponse>;

      if (process.env.NODE_ENV === 'development') {
        console.log('[Solomon] Approval response:', apiResponse.data);
      }

      return apiResponse.data;
    } catch (error) {
      console.error('[Solomon] Failed to process approval:', error);
      throw error;
    }
  },
};

export default solomonService;
